import { useSnapshot } from "valtio"
import type { Key, CreatedTreeState, SelectedMap } from "./types"

export type UseSelected = {
  selections: SelectedMap
  select: (key: Key) => void
  unselect: (key: Key) => void
  handleRange: (requestKey: Key, modifier: boolean) => void
}

export function useSelected<T>(ts: CreatedTreeState<T>): UseSelected {
  const selectedState = ts.selected
  const treeSnap = useSnapshot(ts.tree)
  const selections = useSnapshot(ts.selected)

  const [lastKey, setLastKey] = ts.useLastKey()

  function handleRange(requestKey: Key, modifier: boolean) {
    if (requestKey === lastKey) return

    //shift+click
    if (modifier) {
      if (lastKey === null) {
        setLastKey(requestKey)
        return
      }

      if (!ts.areSiblings(requestKey, lastKey)) {
        setLastKey(requestKey)
        return
      }

      const keys = ts.getSiblings(requestKey)
      const i1 = keys.indexOf(lastKey)
      const i2 = keys.indexOf(requestKey)
      const range = keys.slice(Math.min(i1, i2), Math.max(i1, i2) + 1)
      range.forEach((key) => {
        selectedState[key] = Boolean(selections[lastKey])
      })

      setLastKey(null)
    } else {
      setLastKey(requestKey)
    }
  }

  function select(key: Key) {
    const down = (key: Key) => {
      const node = treeSnap[key]
      if (!node) return
      selectedState[key] = true
      if (!node.children) return
      node.children.forEach(down)
    }
    down(key)
  }

  function unselect(key: Key) {
    const down = (key: Key) => {
      const node = treeSnap[key]
      if (!node) return
      delete selectedState[key]
      if (!node.children) return
      node.children.forEach(down)
    }
    const up = (key: Key) => {
      const node = treeSnap[key]
      if (!node) return
      if (!node.parentId) return
      delete selectedState[node.parentId]
      up(node.parentId)
    }

    down(key)
    up(key)
  }

  return {
    selections,
    select,
    unselect,
    handleRange,
  }
}
